<template>
  <div class="settings-page">
    <div class="page-header">
      <div class="header-content">
        <h1><i class="fas fa-cog"></i> Organisation Settings</h1>
        <p>Configure retention, retry limits, digest notifications, and per-mailbox overrides</p>
      </div>
    </div>

    <div v-if="store.error" class="alert alert-error">
      <i class="fas fa-exclamation-circle"></i> {{ store.error }}
      <button class="btn-close" @click="store.clearError()">×</button>
    </div>

    <div v-if="store.loading && !store.settings" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i> Loading settings…
    </div>

    <template v-if="store.settings">
      <!-- Organisation Settings Section -->
      <div class="settings-card">
        <h2>Organisation Settings</h2>
        <form @submit.prevent="saveOrgSettings">
          <div v-if="orgErrors.length" class="alert alert-error">
            <ul>
              <li v-for="err in orgErrors" :key="err.propertyPath">
                <strong>{{ err.propertyPath }}</strong>: {{ err.message }}
              </li>
            </ul>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Audit Retention Days *</label>
              <input v-model.number="orgForm.auditRetentionDays" type="number" min="7" max="365" class="form-control" required />
              <small>7–365 days. Records older than this are deleted by the cleanup job.</small>
            </div>

            <div class="form-group">
              <label>Default Max Retries *</label>
              <input v-model.number="orgForm.defaultMaxRetries" type="number" min="1" max="10" class="form-control" required />
              <small>1–10. Default for all mailboxes (can be overridden per mailbox below).</small>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Digest Email</label>
              <input v-model="orgForm.digestEmail" type="email" class="form-control" placeholder="ops@example.com (leave blank to disable)" />
              <small>Daily digest is sent to this address. Leave empty to disable.</small>
            </div>

            <div class="form-group">
              <label>Digest Hour (0–23)</label>
              <select v-model.number="orgForm.digestHour" class="form-control">
                <option v-for="h in 24" :key="h - 1" :value="h - 1">{{ String(h - 1).padStart(2, '0') }}:00</option>
              </select>
              <small>Hour of day (in organisation timezone) when digest is sent.</small>
            </div>
          </div>

          <div class="form-group">
            <label>Timezone</label>
            <input v-model="orgForm.timezone" type="text" class="form-control" placeholder="UTC" />
            <small>Valid IANA timezone (e.g. Europe/Paris, America/New_York). Default: UTC.</small>
          </div>

          <div class="form-footer">
            <button type="submit" class="btn btn-primary" :disabled="store.loading">
              <i v-if="store.loading" class="fas fa-spinner fa-spin"></i>
              Save Organisation Settings
            </button>
            <span v-if="orgSaveSuccess" class="save-success">
              <i class="fas fa-check-circle"></i> Saved
            </span>
          </div>
        </form>
      </div>

      <!-- Per-Mailbox Retry Overrides -->
      <div class="settings-card">
        <h2>Per-Mailbox Retry Overrides</h2>
        <p class="section-desc">
          Set a custom max retry count per mailbox. Leave blank to use the organisation default
          (<strong>{{ store.settings.defaultMaxRetries }}</strong>).
        </p>

        <div v-if="!emailAccounts.length" class="empty-state">
          <p>No mailboxes configured.</p>
        </div>

        <div v-else class="mailbox-table-wrapper" data-scroll-x>
        <table class="mailbox-table">
          <thead>
            <tr>
              <th>Mailbox</th>
              <th>Override Max Retries</th>
              <th>Effective Limit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="acc in emailAccounts" :key="acc.id">
              <td>{{ acc.label || acc.email }}</td>
              <td>
                <input
                  v-model.number="mailboxRetryInputs[acc.id]"
                  type="number"
                  min="1"
                  max="10"
                  class="form-control form-control-sm"
                  placeholder="org default"
                  @input="mailboxRetryInputs[acc.id] = $event.target.value === '' ? null : Number($event.target.value)"
                />
              </td>
              <td>
                <span v-if="mbSettings(acc.id)">
                  {{ mbSettings(acc.id).effectiveMaxRetryCount }}
                </span>
                <span v-else class="text-muted">{{ store.settings.defaultMaxRetries }}</span>
              </td>
              <td>
                <button class="btn btn-sm btn-primary" @click="saveMailboxSettings(acc.id)">
                  Save
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
        <div v-if="mailboxSaveSuccess" class="save-success mt-8">
          <i class="fas fa-check-circle"></i> Mailbox settings saved
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useOrganisationSettingsStore } from '../stores/organisationSettings.js'
import { useEmailAccountsStore } from '../stores/emailAccounts.js'

const store = useOrganisationSettingsStore()
const emailAccountsStore = useEmailAccountsStore()

const orgForm = ref({
  auditRetentionDays: 90,
  defaultMaxRetries: 3,
  digestEmail: '',
  digestHour: 7,
  timezone: 'UTC',
})

const orgErrors = ref([])
const orgSaveSuccess = ref(false)
const mailboxSaveSuccess = ref(false)
const mailboxRetryInputs = ref({})

const emailAccounts = computed(() => emailAccountsStore.accounts)

onMounted(async () => {
  await store.fetchSettings()
  if (store.settings) {
    orgForm.value = {
      auditRetentionDays: store.settings.auditRetentionDays,
      defaultMaxRetries: store.settings.defaultMaxRetries,
      digestEmail: store.settings.digestEmail ?? '',
      digestHour: store.settings.digestHour,
      timezone: store.settings.timezone,
    }
  }

  if (!emailAccountsStore.accounts.length) {
    await emailAccountsStore.loadAccounts()
  }

  for (const acc of emailAccountsStore.accounts) {
    await store.fetchMailboxSettings(acc.id)
    const ms = store.mailboxSettings[acc.id]
    mailboxRetryInputs.value[acc.id] = ms?.maxRetryCount ?? null
  }
})

async function saveOrgSettings() {
  orgErrors.value = []
  orgSaveSuccess.value = false
  const result = await store.saveSettings({
    ...orgForm.value,
    digestEmail: orgForm.value.digestEmail || null,
  })
  if (result.success) {
    orgSaveSuccess.value = true
    setTimeout(() => { orgSaveSuccess.value = false }, 3000)
  } else if (result.violations) {
    orgErrors.value = result.violations
  }
}

async function saveMailboxSettings(emailAccountId) {
  mailboxSaveSuccess.value = false
  const value = mailboxRetryInputs.value[emailAccountId]
  await store.saveMailboxSettings(emailAccountId, {
    maxRetryCount: value !== null && value !== '' ? value : null,
  })
  mailboxSaveSuccess.value = true
  setTimeout(() => { mailboxSaveSuccess.value = false }, 3000)
}

function mbSettings(emailAccountId) {
  return store.mailboxSettings[emailAccountId] ?? null
}
</script>

<style scoped>
.settings-page { padding: 24px; }
.page-header { margin-bottom: 24px; }
.page-header h1 { font-size: 1.5rem; font-weight: 600; margin: 0 0 4px; }
.page-header p { margin: 0; color: #6b7280; font-size: 0.9rem; }
.settings-card { background: white; border: 1px solid #e5e7eb; border-radius: 8px; padding: 24px; margin-bottom: 24px; }
.settings-card h2 { font-size: 1.1rem; font-weight: 600; margin: 0 0 20px; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
.section-desc { color: #6b7280; font-size: 0.875rem; margin: -12px 0 16px; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.form-group { margin-bottom: 16px; }
.form-group label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 0.875rem; color: #374151; }
.form-group small { display: block; margin-top: 4px; color: #9ca3af; font-size: 0.78rem; }
.form-control { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; }
.form-control-sm { padding: 5px 8px; font-size: 0.8rem; width: 100px; }
.form-footer { display: flex; align-items: center; gap: 12px; margin-top: 8px; }
.alert { padding: 12px 16px; border-radius: 6px; margin-bottom: 16px; }
.alert-error { background: #fee2e2; color: #991b1b; border: 1px solid #fca5a5; }
.alert-error ul { margin: 4px 0 0 16px; }
.loading-state { text-align: center; padding: 48px; color: #6b7280; }
.empty-state { text-align: center; padding: 24px; color: #9ca3af; }
.mailbox-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.mailbox-table th, .mailbox-table td { padding: 10px 12px; text-align: left; border-bottom: 1px solid #e5e7eb; }
.mailbox-table th { background: #f9fafb; font-weight: 600; color: #374151; }
.btn { padding: 8px 16px; border: none; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 500; display: inline-flex; align-items: center; gap: 6px; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-primary { background: #3b82f6; color: white; }
.btn-sm { padding: 4px 10px; font-size: 0.8rem; }
.btn-close { background: none; border: none; font-size: 1.2rem; cursor: pointer; color: #6b7280; margin-left: auto; }
.text-muted { color: #9ca3af; }
.save-success { color: #065f46; font-size: 0.875rem; display: flex; align-items: center; gap: 4px; }
.mt-8 { margin-top: 8px; }
.mailbox-table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.form-row > * { min-width: 0; }
.alert { overflow-wrap: anywhere; }
.settings-card h2 { overflow-wrap: anywhere; }

@media (max-width: 991.98px) {
  .settings-page { padding: 0; }
}

@media (max-width: 767.98px) {
  .page-header { text-align: left; margin-bottom: 16px; }
  .settings-card { padding: 16px; margin-bottom: 16px; }
  .form-row { grid-template-columns: 1fr; gap: 0; }
  .form-footer { flex-wrap: wrap; }
  .form-footer .btn { flex: 1 1 100%; justify-content: center; min-height: 44px; }
  .mailbox-table { min-width: 480px; }
  .mailbox-table th, .mailbox-table td { padding: 8px 10px; }
}

@media (pointer: coarse) {
  .btn-close { min-width: 44px; min-height: 44px; }
  .mailbox-table .btn-sm { min-height: 44px; }
}
</style>
